/**
 * D&D 5e creature size categories, mapped onto VTT grid cells.
 *
 * The grid cell in grid.ts is 64 map units = one 5 ft square. Creature
 * footprints (PHB p.191) translate as:
 *
 *   Tiny       2.5 ft × 2.5 ft   → 0.5 × 0.5 cells
 *   Small      5   ft × 5   ft   → 1   × 1   cells
 *   Medium     5   ft × 5   ft   → 1   × 1   cells
 *   Large      10  ft × 10  ft   → 2   × 2   cells
 *   Huge       15  ft × 15  ft   → 3   × 3   cells
 *   Gargantuan 20  ft × 20  ft   → 4   × 4   cells
 *
 * Small and Medium share a cell footprint; they're both in the list
 * because the picker UI shows them as separate choices even though
 * they snap to the same square.
 *
 * Images are placed in a SIZE × SIZE box and the renderer fits the
 * natural aspect inside that box (see drawImage). This keeps tokens
 * and map cut-ins visually consistent with the creature they represent.
 */

export const GRID_CELL_MAP_UNITS = 64;

export type SizeCategory =
  | "tiny"
  | "small"
  | "medium"
  | "large"
  | "huge"
  | "gargantuan";

export interface SizeCategoryDef {
  key: SizeCategory;
  label: string;
  /** Side length in grid cells. */
  cells: number;
}

export const SIZE_CATEGORIES: SizeCategoryDef[] = [
  { key: "tiny", label: "Tiny", cells: 0.5 },
  { key: "small", label: "Small", cells: 1 },
  { key: "medium", label: "Medium", cells: 1 },
  { key: "large", label: "Large", cells: 2 },
  { key: "huge", label: "Huge", cells: 3 },
  { key: "gargantuan", label: "Gargantuan", cells: 4 },
];

/** Default size for a freshly-dropped image — Medium feels right for tokens. */
export const DEFAULT_IMAGE_SIZE: SizeCategory = "medium";

/**
 * Side length in map units for a given size category. The image element's
 * width and height both snap to this value; the renderer handles aspect.
 */
export function sizeToMapUnits(size: SizeCategory): number {
  const def = SIZE_CATEGORIES.find((s) => s.key === size);
  if (!def) return GRID_CELL_MAP_UNITS; // medium fallback
  return def.cells * GRID_CELL_MAP_UNITS;
}

/**
 * Given a pair of dimensions (width, height), pick the size category whose
 * footprint most closely matches. Used to show the current size in the
 * picker when an image element is selected.
 */
export function matchSize(width: number, height: number): SizeCategory {
  const side = Math.max(Math.abs(width), Math.abs(height));
  let best: SizeCategory = "medium";
  let bestDelta = Infinity;
  for (const s of SIZE_CATEGORIES) {
    const d = Math.abs(side - s.cells * GRID_CELL_MAP_UNITS);
    if (d < bestDelta) {
      bestDelta = d;
      best = s.key;
    }
  }
  return best;
}
